import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Wand2, Save, ArrowUpRight, Check, Hash, FileText, Image as ImageIcon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { Badge } from '../components/ui/Badge';
import { ImageVariantGrid, type ImageVariant } from '../components/widgets/ImageVariantGrid';
import { Drafts, Listings as ListingsApi, type Draft } from '../api/client';
import { useStore } from '../store';

export function DraftStudio() {
  const { id } = useParams();
  const [brief, setBrief] = useState('Handmade lavender soap — cold pressed, vegan, gift-ready, small batch');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedCopy, setSelectedCopy] = useState(0);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const pushToast = useStore((s) => s.pushToast);

  useEffect(() => {
    if (id) {
      Drafts.get(Number(id)).then(setDraft).catch(() => setDraft(null));
    }
  }, [id]);

  const handleGenerate = async () => {
    if (!brief.trim()) {
      pushToast({ kind: 'error', message: 'Brief is required' });
      return;
    }
    setLoading(true);
    try {
      const d = await Drafts.create({ brief, n_images: 4, n_copy: 3 });
      setDraft(d);
      setSelectedImage(d.image_variants[0]?.id ?? null);
      setSelectedCopy(0);
      pushToast({ kind: 'success', message: 'Draft generated — review and save' });
    } catch (e) {
      pushToast({ kind: 'error', message: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!draft) return;
    const pick = draft.copy_variants[selectedCopy];
    if (!pick?.title) {
      pushToast({ kind: 'error', message: 'Pick a copy variant first' });
      return;
    }
    const tags = (pick.tags ?? '').split(',').map((t) => t.trim()).filter(Boolean).slice(0, 13);
    setPublishing(true);
    try {
      const r = await ListingsApi.create({
        title: pick.title,
        description: pick.description ?? '',
        price_amount: 17.99, // sensible default; user can edit before going live
        price_currency: 'USD',
        quantity: 10,
        tags,
        who_made: 'i_did',
        when_made: 'made_to_order',
        is_digital: false,
      } as any);
      const persisted = (r as any).persisted;
      pushToast({
        kind: 'success',
        message: persisted === 'local'
          ? 'Saved locally — connect Etsy to publish'
          : `Published to Etsy`,
      });
    } catch (e) {
      pushToast({ kind: 'error', message: (e as Error).message });
    } finally {
      setPublishing(false);
    }
  };

  const selectedImageUrl = draft?.image_variants.find((v) => v.id === selectedImage)?.url;

  return (
    <div>
      <PageHeader
        kicker="// draft studio · 03"
        title="From brief to listing"
        description="One prompt. Four image variants, three copy variants, a tag set — ready to review and push to Etsy."
        actions={
          <Button variant="primary" leftIcon={<Sparkles size={13} />} onClick={handleGenerate} loading={loading}>
            {loading ? 'Generating…' : draft ? 'Regenerate' : 'Generate draft'}
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-4">
        {/* Left: brief input + steps */}
        <div className="space-y-4">
          <Card>
            <CardHeader kicker="// step 01" title="Brief" description="What is this listing? Be specific." />
            <Field
              kicker="// input"
              label="Product brief"
              hint="E.g. 'Handmade lavender soap, gift-ready, vegan, cold-pressed'"
            >
              <textarea
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                rows={4}
                placeholder="Describe the product, vibe, audience, materials…"
                className="resize-none"
              />
            </Field>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {['Handmade soap', 'Vintage poster', 'Wedding invite', 'Knit beanie', 'Ceramic mug'].map((s) => (
                <button
                  key={s}
                  onClick={() => setBrief(s)}
                  className="px-2.5 h-7 rounded-lg bg-cream-100 border border-cream-300 text-[11px] text-ink-600 hover:text-ink-800 hover:border-etsy-200 hover:bg-etsy-50 transition"
                >
                  {s}
                </button>
              ))}
            </div>
          </Card>

          <Card noise>
            <CardHeader kicker="// pipeline" title="What runs" description="Transparent steps." />
            <ol className="space-y-2 text-[12.5px]">
              <PipelineStep n="01" title="Brief intake" desc="Parse product, vibe, audience" done />
              <PipelineStep n="02" title="Image gen" desc="4 variants via Codex Image 2" done={!!draft} />
              <PipelineStep n="03" title="Copy gen" desc="3 titles + 3 descriptions via minimax M3" done={!!draft} />
              <PipelineStep n="04" title="Tag gen" desc="13 tags tuned for Etsy search" done={!!draft} />
              <PipelineStep n="05" title="Save & publish" desc="Push to Etsy shop" />
            </ol>
          </Card>
        </div>

        {/* Right: results */}
        <div className="space-y-4 min-w-0">
          {!draft ? (
            <EmptyStudio onGenerate={handleGenerate} loading={loading} brief={brief} />
          ) : (
            <>
              <Card>
                <CardHeader
                  kicker="// step 02"
                  title="Image variants"
                  description="Click to select. Use the regen icon for fresh options."
                  actions={<Badge tone="brand">{draft.image_variants.length} variants</Badge>}
                />
                <ImageVariantGrid
                  variants={draft.image_variants as ImageVariant[]}
                  selected={selectedImage}
                  onSelect={setSelectedImage}
                  onRegenerate={handleGenerate}
                />
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader
                    kicker="// step 03"
                    title="Copy variants"
                    description="Pick the strongest angle."
                    actions={<Badge tone="brand">{draft.copy_variants.length} variants</Badge>}
                  />
                  <div className="space-y-2">
                    {draft.copy_variants.map((c, i) => (
                      <CopyVariant
                        key={c.id}
                        title={c.title ?? ''}
                        description={c.description ?? ''}
                        tags={c.tags}
                        selected={selectedCopy === i}
                        onClick={() => setSelectedCopy(i)}
                        index={i}
                      />
                    ))}
                  </div>
                </Card>

                <Card>
                  <CardHeader
                    kicker="// step 04"
                    title="Tags & taxonomy"
                    description="Optimized for Etsy search."
                    actions={<Badge tone="brand">13 / 13</Badge>}
                  />
                  <div className="flex flex-wrap gap-1.5">
                    {(draft.copy_variants[selectedCopy]?.tags?.split(',').filter(Boolean) ?? []).map((t) => (
                      <span
                        key={t}
                        className="px-2.5 h-7 inline-flex items-center gap-1 rounded-lg bg-etsy-50 border border-etsy-200 text-[11.5px] text-etsy-700 font-mono"
                      >
                        <Hash size={10} className="opacity-60" />
                        {t.trim()}
                      </span>
                    ))}
                  </div>

                  <div className="divider my-4" />

                  <div className="space-y-2">
                    <div className="kicker">trending</div>
                    <div className="flex flex-wrap gap-1">
                      {['handmade gift', 'small batch', 'natural soap', 'vegan beauty', 'eco gift'].map((t) => (
                        <button
                          key={t}
                          className="px-2 h-6 rounded-md text-[10.5px] font-mono text-ink-500 hover:text-etsy hover:bg-etsy-50 transition flex items-center gap-0.5"
                        >
                          + {t}
                        </button>
                      ))}
                    </div>
                  </div>
                </Card>
              </div>

              <Card noise>
                <CardHeader kicker="// step 05" title="Preview & publish" description="What gets sent to Etsy." />
                <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-5">
                  <div className="aspect-square rounded-2xl overflow-hidden border border-cream-300 bg-cream-100">
                    {selectedImageUrl && <img src={selectedImageUrl} alt="preview" className="w-full h-full object-cover" />}
                  </div>
                  <div>
                    <div className="kicker mb-1.5">title</div>
                    <div className="serif text-[17px] text-ink-800 mb-3 leading-snug">
                      {draft.copy_variants[selectedCopy]?.title ?? '—'}
                    </div>
                    <div className="kicker mb-1.5">description</div>
                    <div className="text-[12.5px] text-ink-700 leading-relaxed tracking-tight whitespace-pre-line max-h-32 overflow-y-auto scroll-soft pr-2">
                      {draft.copy_variants[selectedCopy]?.description ?? '—'}
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex items-center justify-between gap-3">
                  <div className="kicker text-ink-400">draft #{draft.id} · created {new Date(draft.created_at * 1000).toLocaleString()}</div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="primary"
                      leftIcon={<ArrowUpRight size={13} />}
                      loading={publishing}
                      onClick={handlePublish}
                    >
                      {publishing ? 'Publishing…' : 'Publish to Etsy'}
                    </Button>
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PipelineStep({ n, title, desc, done }: { n: string; title: string; desc: string; done?: boolean }) {
  return (
    <li className="flex items-start gap-3">
      <div
        className={
          'h-7 w-7 shrink-0 rounded-lg flex items-center justify-center text-[10.5px] font-mono font-medium ' +
          (done
            ? 'bg-etsy text-white shadow-[0_4px_10px_-2px_rgba(241,100,30,0.4)]'
            : 'bg-cream-100 border border-cream-300 text-ink-500')
        }
      >
        {done ? <Check size={12} strokeWidth={2.5} /> : n}
      </div>
      <div className="-mt-0.5">
        <div className="text-[12.5px] text-ink-800 tracking-tight font-medium">{title}</div>
        <div className="text-[11.5px] text-ink-500">{desc}</div>
      </div>
    </li>
  );
}

function CopyVariant({ title, description, tags, selected, onClick, index }: { title: string; description: string; tags?: string; selected: boolean; onClick: () => void; index: number }) {
  return (
    <button
      onClick={onClick}
      className={
        'w-full text-left p-3.5 rounded-xl border transition ' +
        (selected
          ? 'border-etsy bg-etsy-50/40 shadow-[0_0_0_3px_rgba(241,100,30,0.10),0_2px_8px_-2px_rgba(241,100,30,0.15)]'
          : 'border-cream-300 bg-white hover:border-etsy-200 hover:bg-cream-50/40')
      }
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="kicker">v{index + 1}</div>
        {selected && <Badge tone="brand">selected</Badge>}
      </div>
      <div className="text-[12.5px] text-ink-800 tracking-tight mb-1.5 line-clamp-1 font-medium">{title}</div>
      <div className="text-[11.5px] text-ink-500 line-clamp-2 tracking-tight leading-relaxed">{description}</div>
    </button>
  );
}

function EmptyStudio({ onGenerate, loading, brief }: { onGenerate: () => void; loading: boolean; brief: string }) {
  return (
    <Card className="min-h-[480px] flex items-center justify-center relative overflow-hidden" noise>
      <div className="absolute inset-0 bg-warm-mesh opacity-80" />
      <div className="relative text-center max-w-md">
        <div className="inline-flex h-16 w-16 rounded-3xl bg-gradient-to-br from-etsy-300 to-etsy-500 border border-etsy-200 items-center justify-center mb-6 shadow-[0_12px_32px_-8px_rgba(241,100,30,0.45)]">
          <Wand2 size={26} className="text-white" strokeWidth={1.7} />
        </div>
        <div className="kicker kicker-brand mb-2">// ready</div>
        <h2 className="serif text-[26px] text-ink-800 mb-2 font-semibold">Brief in. Listing out.</h2>
        <p className="text-[13px] text-ink-600 mb-6 leading-relaxed tracking-tight">
          We&rsquo;ll generate 4 image variants, 3 title/description options, and a tuned tag set — all from your brief:
        </p>
        <div className="surface-soft p-4 text-left mb-6 border-etsy-200 bg-etsy-50/40">
          <div className="kicker kicker-brand mb-1.5">your brief</div>
          <div className="text-[13px] text-ink-700 italic serif">&ldquo;{brief}&rdquo;</div>
        </div>
        <Button variant="primary" size="lg" leftIcon={<Sparkles size={14} />} onClick={onGenerate} loading={loading}>
          {loading ? 'Generating…' : 'Generate draft'}
        </Button>
        <div className="mt-4 kicker text-ink-500">~ 4 images + 3 copies + 13 tags · &lt; 90s</div>
      </div>
    </Card>
  );
}
